<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import { cn } from '@/lib/utils';

interface Props {
  src: string;
  class?: string;
  imageClass?: string;
  alt?: string;
  width?: string | number;
  height?: string | number;
  loading?: 'lazy' | 'eager';
  draggable?: boolean;
  rootMargin?: string;
  overflowHidden?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  width: '100%',
  height: 'auto',
  loading: 'lazy',
  draggable: true,
  rootMargin: '100px',
  overflowHidden: true,
});

const emit = defineEmits<{
  (e: 'error', event: Event): void;
  (e: 'load', event: Event): void;
}>();

const containerRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);
const isLoaded = ref(false);

let stopObserver: (() => void) | null = null;

onMounted(() => {
  // Use IntersectionObserver to delay the rendering of the actual img tag
  // if we want more control, or just for the fade-in effect.
  // For SEO, we use noscript fallback.
  const { stop } = useIntersectionObserver(
    containerRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting) {
        isVisible.value = true;
        stop();
      }
    },
    {
      rootMargin: props.rootMargin, // Use the configurable rootMargin
    }
  );
  stopObserver = stop;
});

onUnmounted(() => {
  if (stopObserver) stopObserver();
});

const handleLoad = (e: Event) => {
  isLoaded.value = true;
  emit('load', e);
};

const handleError = (e: Event) => {
  emit('error', e);
};
</script>

<template>
  <div
    ref="containerRef"
    :class="cn('image-lazy-container', props.overflowHidden && 'overflow-hidden', props.class)"
  >
    <!-- 
      The actual image tag. 
      isVisible controls when the src is actually assigned to trigger loading.
      We use v-if to avoid any early network request, but maintain SEO via noscript.
    -->
    <img
      v-if="isVisible"
      :src="src"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="loading"
      :draggable="draggable"
      decoding="async"
      @load="handleLoad"
      @error="handleError"
      :class="
        cn(
          'transition-opacity duration-500 ease-in-out',
          isLoaded ? 'opacity-100' : 'opacity-0',
          imageClass
        )
      "
    />

    <!-- SEO Fallback: Crawlers that don't execute JS will see this -->
    <noscript>
      <img
        :src="src"
        :alt="alt"
        :width="width"
        :height="height"
        :class="cn('w-full h-full object-cover', imageClass)"
      />
    </noscript>
  </div>
</template>

<style scoped>
.image-lazy-container {
  /* Ensure container has some size if width/height are provided as numbers */
  width: v-bind('typeof width === "number" ? `${width}px` : width');
  height: v-bind('typeof height === "number" ? `${height}px` : height');
}
</style>
